/** SRS #16 — entitled-jurisdiction navigation helpers shared by the map and dashboards.
 *
 * Entitlements arrive as jurisdiction *names* (what the plan/admin stores);
 * the map works in ISO-2. The name → code rule mirrors the server's
 * `jurisdiction_match_sql`: exact, or a prefix that ends at a word boundary,
 * so "United States" covers "United States (Federal)" while "India" does not
 * cover "Indiana". */

export const SUPRANATIONALS: Record<string, string> = {
  EU: 'European Union', UN: 'United Nations', OECD: 'OECD', GCC: 'Gulf Cooperation Council',
  ASEAN: 'ASEAN', APEC: 'APEC', AU: 'African Union',
};

/** Member states drawn on the choropleth so a supranational jump has bounds. */
export const SUPRANATIONAL_MEMBERS: Record<string, string[]> = {
  EU: ['AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE'],
  GCC: ['AE', 'SA', 'QA', 'BH', 'KW', 'OM'],
  ASEAN: ['BN', 'KH', 'ID', 'LA', 'MY', 'MM', 'PH', 'SG', 'TH', 'VN'],
};

const SUPRA_ALIASES: Record<string, string> = {
  'european union': 'EU', eu: 'EU', 'united nations': 'UN', un: 'UN', oecd: 'OECD',
  'gulf cooperation council': 'GCC', gcc: 'GCC', asean: 'ASEAN', apec: 'APEC', 'african union': 'AU',
};

export function nameMatches(entitled: string, candidate: string): boolean {
  const a = entitled.trim().toLowerCase();
  const b = candidate.trim().toLowerCase();
  return a === b || b.startsWith(`${a} `);
}

/**
 * Resolve entitlement names to the ISO-2 (and supranational) codes they cover.
 * `null` in → `null` out (no restriction). Names that match nothing are
 * dropped: the map cannot draw them, and they would not unlock anything.
 */
export function entitledCodes(
  entitled: string[] | null | undefined,
  countries: { name: string; iso2?: string | null }[],
): string[] | null {
  if (entitled == null) return null;
  const out = new Set<string>();
  for (const value of entitled) {
    const key = value.trim().toLowerCase();
    if (SUPRA_ALIASES[key]) { out.add(SUPRA_ALIASES[key]); continue; }
    if (/^[a-z]{2}$/i.test(value.trim())) { out.add(value.trim().toUpperCase()); continue; }
    for (const c of countries) {
      if (c.iso2 && nameMatches(value, c.name)) out.add(c.iso2.toUpperCase());
    }
  }
  return Array.from(out);
}

export function isAllowed(code: string, allowed: string[] | null | undefined): boolean {
  if (allowed == null) return true;
  return allowed.includes(code.toUpperCase());
}
